import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import AddIcon from '@mui/icons-material/Add';

interface ViewBarProps {
  view: 'grid' | 'vertical';
  onViewChange: (view: 'grid' | 'vertical') => void;
  onCreateNew?: () => void;
  createButtonText?: string;
  // Filter props
  showFilters?: boolean;
  statusFilter?: string;
  scopeFilter?: string;
  returnFilter?: string;
  onStatusFilterChange?: (status: string) => void;
  onScopeFilterChange?: (scope: string) => void;
  onReturnFilterChange?: (returnValue: string) => void;
  // Custom status options
  statusOptions?: { value: string; label: string }[];
}

const ViewBar: React.FC<ViewBarProps> = ({ 
  view, 
  onViewChange, 
  onCreateNew, 
  createButtonText = "Create New",
  showFilters = false,
  statusFilter = '',
  scopeFilter = '',
  returnFilter = '',
  onStatusFilterChange,
  onScopeFilterChange,
  onReturnFilterChange,
  statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'in progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' }
  ]
}) => {
  const handleViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: 'grid' | 'vertical' | null,
  ) => {
    if (newView !== null) {
      onViewChange(newView);
    }
  };

  return (
    <Box sx={{ 
      position: 'fixed',
      top: "63px",
      left: 0,
      right: 0,
      zIndex: 1000,
      backgroundColor: '#1e1e1e',
      borderBottom: '1px solid',
      borderColor: 'divider',
      px: 2,
      py: 1,
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      backdropFilter: 'blur(8px)',
      boxShadow: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={handleViewChange}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <GridViewIcon />
          </ToggleButton>
          <ToggleButton value="vertical" aria-label="vertical view">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        {showFilters && (
          <>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={statusFilter}
                onChange={(e) => onStatusFilterChange?.(e.target.value)}
                displayEmpty
                sx={{ 
                  color: 'white',
                  fontSize: '0.875rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiSelect-select': {
                    padding: '4px 8px',
                    minHeight: 'auto'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#2a2a2a',
                      color: 'white',
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.875rem' }}>Status</MenuItem>
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={scopeFilter}
                onChange={(e) => onScopeFilterChange?.(e.target.value)}
                displayEmpty
                sx={{ 
                  color: 'white',
                  fontSize: '0.875rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiSelect-select': {
                    padding: '4px 8px',
                    minHeight: 'auto'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#2a2a2a',
                      color: 'white',
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.875rem' }}>Scope</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="mid">Mid</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={returnFilter}
                onChange={(e) => onReturnFilterChange?.(e.target.value)}
                displayEmpty
                sx={{ 
                  color: 'white',
                  fontSize: '0.875rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiSelect-select': {
                    padding: '4px 8px',
                    minHeight: 'auto'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#2a2a2a',
                      color: 'white',
                      '& .MuiMenuItem-root': {
                        fontSize: '0.875rem',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.875rem' }}>Return</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="mid">Mid</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
      </Box>
      
      {onCreateNew && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateNew}
          size="small"
          sx={{ 
            backgroundColor: '#4f46e5', 
            '&:hover': { backgroundColor: '#4338ca' } 
          }}
        >
          {createButtonText}
        </Button>
      )}
    </Box>
  );
};

export default ViewBar; 